import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "../../auth/store/authSlice";
import {
  clearSupportActionError,
  createSupportCaseThunk,
  deleteSupportCaseThunk,
  deleteSupportMessageThunk,
  editSupportMessageThunk,
  fetchSupportAttachmentUrlThunk,
  fetchSupportCasesThunk,
  loadSupportCaseThreadThunk,
  selectSupportState,
  sendSupportReplyThunk,
  setSelectedSupportCase,
  updateSupportCaseStatusThunk,
} from "../store/supportSlice";

const getCurrentUserName = (user) => {
  // const first = String(user?.firstname || "").trim();
  // const last = String(user?.lastname || "").trim();

  // if (first || last) {
  //   return `${first} ${last}`.trim();
  // }

  // const email = String(user?.email || "").trim();
  // if (email) {
  //   return email.split("@")[0] || "You";
  // }

  return "You";
};

export const useSupportTickets = () => {
  const dispatch = useDispatch();
  const state = useSelector(selectSupportState);
  const currentUser = useSelector(selectCurrentUser);

  const currentUserName = useMemo(() => getCurrentUserName(currentUser), [currentUser]);

  useEffect(() => {
    if (state.hasLoaded) return;
    dispatch(fetchSupportCasesThunk());
  }, [dispatch, state.hasLoaded]);

  const handleCreateNew = useCallback(() => {
    dispatch(clearSupportActionError());
  }, [dispatch]);

  const handleSubmitRequest = useCallback(
    async (formData) => {
      const action = await dispatch(createSupportCaseThunk({ formData }));
      return createSupportCaseThunk.fulfilled.match(action);
    },
    [dispatch],
  );

  const handleCaseClick = useCallback(
    (caseItem) => {
      dispatch(loadSupportCaseThreadThunk(caseItem));
    },
    [dispatch],
  );

  const handleBackToCases = useCallback(() => {
    dispatch(setSelectedSupportCase(null));
    dispatch(clearSupportActionError());
  }, [dispatch]);

  const handleSendReply = useCallback(
    async (caseId, message, attachment) => {
      const action = await dispatch(
        sendSupportReplyThunk({
          caseId,
          message,
          attachment: attachment || null,
          currentUserName,
        }),
      );

      if (!sendSupportReplyThunk.fulfilled.match(action)) {
        return false;
      }

      await dispatch(loadSupportCaseThreadThunk({ id: caseId }));
      return true;
    },
    [currentUserName, dispatch],
  );

  const handleDownloadAttachment = useCallback(
    async (message) => {
      if (!message?.id) {
        return false;
      }

      const action = await dispatch(
        fetchSupportAttachmentUrlThunk({ messageId: message.id }),
      );

      if (!fetchSupportAttachmentUrlThunk.fulfilled.match(action)) {
        return false;
      }

      const attachmentUrl = action.payload?.attachmentUrl;
      if (!attachmentUrl) {
        return false;
      }

      const link = document.createElement("a");
      link.href = attachmentUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
      return true;
    },
    [dispatch],
  );

  const handleEditMessage = useCallback(
    async (messageId, messageContent) => {
      const action = await dispatch(editSupportMessageThunk({ messageId, messageContent }));
      return editSupportMessageThunk.fulfilled.match(action);
    },
    [dispatch],
  );

  const handleDeleteMessage = useCallback(
    async (messageId) => {
      const action = await dispatch(deleteSupportMessageThunk(messageId));
      return deleteSupportMessageThunk.fulfilled.match(action);
    },
    [dispatch],
  );

  const handleOpenCase = useCallback(
    async (caseId) => {
      await dispatch(updateSupportCaseStatusThunk({ caseId, status: "open" }));
    },
    [dispatch],
  );

  const handleCloseCase = useCallback(
    async (caseId) => {
      await dispatch(updateSupportCaseStatusThunk({ caseId, status: "closed" }));
    },
    [dispatch],
  );

  const handleConfirmDelete = useCallback(
    async (caseId) => {
      await dispatch(deleteSupportCaseThunk(caseId));
    },
    [dispatch],
  );

  return {
    ...state,
    currentUserName,
    handleCreateNew,
    handleSubmitRequest,
    handleCaseClick,
    handleBackToCases,
    handleSendReply,
    handleDownloadAttachment,
    handleEditMessage,
    handleDeleteMessage,
    handleOpenCase,
    handleCloseCase,
    handleConfirmDelete,
  };
};

export default useSupportTickets;
